import { Injectable } from '@angular/core';
import { GlobalService } from '../../../../SGRE-shared/services/global.service';
import { environment } from '../../../../../environments/environment';
import { CartPageTableData, CartsData, CartProductsResponseType, ColumnLabel, ColumnName } from '../../models/custom-cart.model';
import { Column, ColumnType } from '../../../../SGRE-shared/models/tableConfiguration';
import { AppliedProductPromotion } from '../../../checkout/models/checkout.model';

@Injectable()
export class CartEffectsService {

  public imageUrl: string = environment.siteUrls.getBackofficeUrl.slice(0, -1);
  public columns: Column[];

  constructor(
    private globalService: GlobalService
  ) { }

  mapAndConfigureCartData(data: unknown): CartPageTableData {
    const typedData = data as CartProductsResponseType;
    const mappedData = typedData?.entries?.map((item) => this.mapTableData(item, typedData));
    return {
      cartProductsInfo: mappedData,
      priceFetchErrorEntries: typedData?.priceFetchErrorEntries,
      appliedVouchers: typedData?.appliedVouchers,
      columnData: this.createTableConfiguration(),
    };
  }

  checkForAppliedPromotions(entry: CartsData, appliedProductPromotions?: AppliedProductPromotion[]): boolean {
    if (!appliedProductPromotions || appliedProductPromotions.length === 0) {
      return false;
    }
    return appliedProductPromotions.some(promotion => {
      if (!promotion.consumedEntries || promotion.consumedEntries.length === 0) {
        return false;
      }
      return promotion.consumedEntries.some(consumedEntry =>
        consumedEntry.orderEntryNumber === entry.entryNumber
      );
    });
  }

  getAdjustedUnitPrice(entry: CartsData, appliedProductPromotions?: AppliedProductPromotion[]): number | null {
    if (!appliedProductPromotions || appliedProductPromotions.length === 0) {
      return null;
    }
    for (const promotion of appliedProductPromotions) {
      if (!promotion.consumedEntries || promotion.consumedEntries.length === 0) {
        continue;
      }
      const consumedEntry = promotion.consumedEntries.find(ce =>
        ce.orderEntryNumber === entry.entryNumber
      );
      if (consumedEntry && consumedEntry.adjustedUnitPrice !== undefined) {
        return consumedEntry.adjustedUnitPrice;
      }
    }
    return null;
  }

  mapTableData(cartsData: CartsData, data?: CartProductsResponseType): CartsData {
    const product = cartsData?.product || {};
    const isQuotation = cartsData?.isQuotation ?? false;
    const hasValidMessage = cartsData.message && (cartsData.message.toString().includes('8') || cartsData.message.toString().includes('4') || cartsData.message.toString().includes('9'));

    // Check for applied promotions and adjusted unit price
    const hasAppliedPromotions = this.checkForAppliedPromotions(cartsData, data?.appliedProductPromotions);
    const adjustedUnitPrice = hasAppliedPromotions ? this.getAdjustedUnitPrice(cartsData, data?.appliedProductPromotions) : null;

    cartsData = {
      ...cartsData,
      imageUrl: Array.isArray(product.images) && product.images.length > 0 && product.images[0].url
        ? this.imageUrl + product.images[0].url
        : '/images_and_icons/image1.jpg',
      partNumber: product.baseProduct ?? '-',
      productName: product.baseOptions?.[0]?.selected?.name ?? '-',
      variant: product.code?.charAt(product.code.length - 1) ?? '-',
      isQuotation: isQuotation && hasValidMessage,
      leadTime: !isQuotation
        ? product.stock?.estimatedLeadTime
          ? `${product.stock.estimatedLeadTime} Days`
          : null
        : '-',
      unitPrice: !isQuotation && hasValidMessage ? cartsData?.basePrice?.value ?? '-' : '-',
      salesUnit: '',
      total: !isQuotation && hasValidMessage ? cartsData?.totalPrice?.value ?? '-' : '-',
      currency: !isQuotation && hasValidMessage ? cartsData?.totalPrice?.currencyIso ?? '-' : '-',
      navigateUrl: this.globalService.getProductUrl(product.url),
      minQuantity: this.getUpdatedMinQty(product.minQuantity, product.interval),
      maxQuantity: product.maxQuantity > 0 ? product.maxQuantity : 1000,
      step: product.interval > 0 ? product.interval : 1,
      isChecked: true,
      statusRequester: isQuotation,
      statusPurchaser: false,
      status: 'edit',
      isDisabled: !hasValidMessage,
      unitPriceStrike: hasAppliedPromotions,
      adjustedUnitPrice: !isQuotation && adjustedUnitPrice ? adjustedUnitPrice?.toString() : '',
    };

    if (data) {
      cartsData = {
        ...cartsData,
        shippingFee: data.shippingFee ?? null,
        transportFee: data.transportFee ?? null,
        totalTax: data.totalTax ?? null,
        totalPriceWithTax: data.totalPriceWithTax ?? null,
      };
    }
    return cartsData;
  }

  getUpdatedMinQty(minQuantity: number, interval: number): number {
    return this.globalService.getUpdatedMinQty(minQuantity, interval);
  }

  createTableConfiguration(): Column[] {
    let columnData = [
      { label: ColumnLabel.Image, name: ColumnName.ImageUrl, type: ColumnType.image },
      { label: ColumnLabel.PartNumber, name: ColumnName.PartNumber, type: ColumnType.link },
      { label: ColumnLabel.ProductName, name: ColumnName.ProductName, type: ColumnType.link },
      { label: ColumnLabel.Variant, name: ColumnName.Variant, type: ColumnType.text },
      { label: ColumnLabel.LeadTime, name: ColumnName.LeadTime, type: ColumnType.number },
      { label: ColumnLabel.UnitPrice, name: ColumnName.UnitPrice, type: ColumnType.unitPrice },
      { label: ColumnLabel.SalesUnit, name: ColumnName.SalesUnit, type: ColumnType.text },
      { label: ColumnLabel.Quantity, name: ColumnName.Quantity, type: ColumnType.quantity },
      { label: ColumnLabel.Total, name: ColumnName.Total, type: ColumnType.price },
      { label: ColumnLabel.Delete, name: ColumnName.Delete, type: ColumnType.delete },
    ];
    this.columns = columnData.map((item) => new Column(item));
    return this.columns;
  }
}